import React, {useState, useEffect} from 'react'
import { AppDataService } from '../services/AppDataService';
import useInfoModal from '../hooks/useInfoModal';
import { MODAL_TYPE_ENUM } from '../enums/CommonEnums';
import { ContainerRegistrationTable } from '../components/ContainerRegistrationTable';
import SkeletonGroup from '../components/SkeletonGroup';
import { Paper, Text, Center, Badge, Box, Group, Stack, Image, Button } from '@mantine/core';
import { IconBuilding, IconBuildingFactory, IconBuildingSkyscraper, IconBuildingStore, IconBulb, IconEye, IconFile, IconFileTypeXls, IconRefresh, IconUser, IconUserCircle } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';

const AdminViewUserProfile = () => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const openModal = useInfoModal();
  const {id} = useParams();

  useEffect(() => {
    fetchContainerRegistrations().catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    })
  }, []);

  const fetchContainerRegistrations = async () => {
    setLoading(true);

    const userResponse = await AppDataService.AdminGetUser(id);
    const response = await AppDataService.AdminGetUserContainerRegistrations(id);

    if(userResponse.statusCode >= 400) {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, 'Failed to fetch user info.');
      return setLoading(false);
    }
    else {
      setUser(userResponse.data.data);
    }

    if(response.statusCode >= 400) {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, 'Failed to fetch container registrations for user.');
      return setLoading(false);
    }
    else {
      setRegistrations(response.data.registrations);
    }

    setLoading(false);
  }

  const handleClickRefresh = (e) => {
    e.preventDefault();

    fetchContainerRegistrations().catch(err => {
      setLoading(false);
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
    });
  }

  const handleClickHideInactive = (e) => {
    e.preventDefault();

    const activeRegistrations = registrations.filter(x => x.isActive === true);

    setRegistrations(activeRegistrations);
  }

  const handleClickDownloadUserSubmissions = async (e) => {
    e.preventDefault();

    const response = await AppDataService.AdminDownloadUserRegistrations(user.id);
  }

  return (
    <>
      {
        loading && <SkeletonGroup height={600} width={1000} num={1} />
      }
      {
        (user && loading === false) && (
          <Group mb={25} grow align='flex-start'>
            <Paper bg='#f8f9fa' p={30} withBorder shadow='md' radius='lg'>
              <Center mb={25}>
                <IconUserCircle size={50} color='#228be6' />
              </Center>
              <Text mb='md' ta='center' fz={22} color='dimmed'>{user.firstName} {user.lastName}</Text>
              <Group noWrap>
                <Text fw={500} miw={100}>Title</Text>
                <Text size='md'>{user.title}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={100}>Company</Text>
                <Text size='md'>{user.companyName}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={100}>Role</Text>
                <Text size='md'>{user.companyRole}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={100}>Email</Text>
                <Text size='md'>{user.email}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={100}>Phone</Text>
                <Text size='md'>{user.phoneNumber}</Text>
              </Group>
            </Paper>
            <Paper bg='#f8f9fa' p={30} withBorder shadow='md' radius='lg'>
              <Center mb={25}>
                <IconBuildingSkyscraper size={50} color='#228be6' />
              </Center>
              <Text mb='md' ta='center' fz={22} color='dimmed'>{user.companyName}</Text>
              <Group noWrap>
                <Text fw={500} miw={150}>Address</Text>
                <Text size='md'>{user.companyAddressLine1}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={150}>City</Text>
                <Text size='md'>{user.companyCity}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={150}>State</Text>
                <Text size='md'>{user.companyState}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={150}>Zipcode</Text>
                <Text size='md'>{user.companyZipCode}</Text>
              </Group>
              <Group noWrap>
                <Text fw={500} miw={150}>OBRC Member</Text>
                <Text size='md'>{user.isOBRCMember.toString() === "true" ? "Yes" : "No"}</Text>
              </Group>
            </Paper>
          </Group>
        )
      }
      {
        (registrations.length > 0 && loading === false) && (
          <>
            <Group mb='md'>
              <Button compact loading={loading} onClick={handleClickRefresh}><IconRefresh size={15} /></Button>
              <Button leftIcon={<IconFileTypeXls size={15} />} onClick={handleClickDownloadUserSubmissions}>Download Submissions</Button>
              <Button color='dark' leftIcon={<IconEye size={15} />} onClick={handleClickHideInactive}>Hide Inactive</Button>
            </Group>
            <ContainerRegistrationTable tableData={registrations} readonly /> 
          </>
        )
      } 
      {
        (registrations.length === 0 && loading === false) && (
          <Paper shadow='md' withBorder radius='lg' maw={800} mx='auto' mt={50} p={50}>
            <Center>
              <IconBulb stroke={1.5} color='#228be6' size={100} />
            </Center>
            <Text fz={20} mt={25} ta='center'>This user does not have any container submissions.</Text>
          </Paper>
        )
      }
    </>
  )
}

export default AdminViewUserProfile;